import { Avatar, Box, Stack, Typography } from '@mui/material'
import DateRangeIcon from '@mui/icons-material/DateRange'
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined'
import ShelterIcon from '@mui/icons-material/NightShelterOutlined'
import CheckListIcon from '@mui/icons-material/FormatListBulleted'
import FamilyIcon from '@mui/icons-material/FamilyRestroom'
import S3Avatar from 'Components/S3Image/S3Avatar'
import { BirthdayToAgeInYearsAndMonths } from 'core/Utilities'
import { childName } from 'Pages/CaregiverPortal/PortalHelpers'
import { type ChildMemberDetails } from 'Pages/CaregiverPortal/Members/ChildDisplay'
import { type ChildRelationship } from 'Pages/CaregiverPortal/Members/ChildRelationships'

export interface ChildProfileCardProps {
  child: ChildMemberDetails
  raceDisplay: string
  showIEP: boolean
  showIFSP: boolean
  showEOIS: boolean
  relationships: ChildRelationship[]
}

function formatLongDate (d: Date): string {
  return new Date(d).toLocaleString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
}

export function ChildProfileCard (props: ChildProfileCardProps): JSX.Element {
  const { child } = props
  const relationships = props.relationships.filter(r => r.id != null && r.relationshipID != null)

  const detailItems: string[] = []
  if (!child.unborn) {
    if (props.raceDisplay !== '') {
      detailItems.push(props.raceDisplay)
    }
    detailItems.push(child.primaryLanguage !== '' ? child.primaryLanguage : 'English')
    if (props.showIEP && child.hasIEP) detailItems.push('Has an IEP')
    if (props.showIFSP && child.hasIFSP) detailItems.push('Has an IFSP')
    if (props.showEOIS && child.hasEOIS) detailItems.push('Has an EOIS')
    if (child.fosterChild) detailItems.push('Foster Child')
    if (child.hasSNAPBenefits) detailItems.push('SNAP Benefits (food assistance)')
    if (child.isOnCashAssistance) detailItems.push('Cash Assistance')
    if (child.isOnSSI) detailItems.push('SSI (Social Security Income)')
  }

  return (
    <Box
      data-testid="childProfileCard"
      sx={{ backgroundColor: '#ECEDEF', borderRadius: '10px', p: '20px 16px', display: 'flex', flexDirection: 'column', gap: '12px' }}
    >
      <Stack direction='row' spacing={1} alignItems='center'>
        {child.imageKey != null && child.imageKey !== ''
          ? <S3Avatar imageKey={child.imageKey} height='32px' width='32px' />
          : <Avatar sx={{ height: '32px', width: '32px' }} />}
        <Typography sx={{ color: '#222024', fontSize: '14px', fontWeight: 600 }} data-testid='childNameTextDisplay'>
          {childName(child)}
        </Typography>
      </Stack>

      <Stack direction='row' spacing={2} alignItems='center'>
        <DateRangeIcon sx={{ color: '#222024' }} />
        {child.unborn
          ? (
            <Stack direction='row' spacing={2} alignItems='center' flexWrap='wrap'>
              <Typography sx={{ color: '#222024', fontSize: '14px' }} data-testid='dueDateTextDisplay'>
                {child.dueDate != null ? formatLongDate(child.dueDate) : ''}
              </Typography>
              <Typography sx={{ color: '#514F53', fontSize: '12px' }}>
                ({BirthdayToAgeInYearsAndMonths(child.dueDate, true)})
              </Typography>
            </Stack>
            )
          : (
            <Stack direction='row' spacing={2} alignItems='center' flexWrap='wrap'>
              <Typography sx={{ color: '#222024', fontSize: '14px' }} data-testid='dateOfBirthTextDisplay'>
                {child.dateOfBirth != null ? formatLongDate(child.dateOfBirth) : ''}
              </Typography>
              <Typography sx={{ color: '#514F53', fontSize: '12px' }}>
                ({BirthdayToAgeInYearsAndMonths(child.dateOfBirth, false)})
              </Typography>
            </Stack>
            )}
      </Stack>

      {child.location != null && (
        <Stack direction='row' spacing={2} alignItems='flex-start'>
          {child.location.isHomeless === true ? <ShelterIcon sx={{ color: '#222024' }} /> : <PlaceOutlinedIcon sx={{ color: '#222024' }} />}
          <Box data-testid='addressTextDisplay'>
            {child.location.isHomeless === true
              ? <Typography sx={{ color: '#222024', fontSize: '14px' }} data-testid='homelessTextDisplay'>Experiencing Homelessness</Typography>
              : <Typography sx={{ color: '#222024', fontSize: '14px' }}>{child.location.address}</Typography>}
            <Typography sx={{ color: '#222024', fontSize: '14px' }}>{child.location.city}, {child.location.state}</Typography>
            <Typography sx={{ color: '#222024', fontSize: '14px' }}>{child.location.zipCode}</Typography>
          </Box>
        </Stack>
      )}

      {detailItems.length > 0 && (
        <Stack direction='row' spacing={2} alignItems='flex-start'>
          <CheckListIcon sx={{ color: '#222024' }} />
          <Box data-testid='detailsTextDisplay'>
            {detailItems.map((item) => (
              <Typography key={item} sx={{ color: '#222024', fontSize: '14px' }}>{item}</Typography>
            ))}
          </Box>
        </Stack>
      )}

      {relationships.length > 0 && (
        <Stack direction='row' spacing={2} alignItems='flex-start'>
          <FamilyIcon sx={{ color: '#222024' }} />
          <Box data-testid='familyTextDisplay'>
            {relationships.map((r) => (
              <Typography key={r.id ?? r.caregiverID} sx={{ color: '#222024', fontSize: '14px' }}>
                {r.caregiverName} - {r.relationshipName}
              </Typography>
            ))}
          </Box>
        </Stack>
      )}
    </Box>
  )
}
